import styled from "styled-components";
import BackButton from "../../../../../components/ui-kit/button/BackButton";
import { useNavigate } from "react-router-dom";
import {
  DropdownFormField,
  TextFormInput,
} from "../../../../../components/form-fields";
import { useForm } from "react-hook-form";
import { DatePickerFormField } from "../../../../../components/form-fields/DatePickerFormField";
import {
  Button,
  ButtonVariant,
  Counter,
  ModalHeader,
  Typography,
  TypographyVariant,
} from "../../../../../components/ui-kit";
import { StripeCouponType } from "../../types";
import { useModalManager } from "../../../../../context/ModalManager";
import { CompaniesList } from "../../../companies/list";
import { useState } from "react";
import { Cross } from "../../../../../components/icons";
import api from "../../../../../util/api";
import { yupResolver } from "@hookform/resolvers/yup";
import { promoCreateSchema } from "./validation";
import { AppRoutes } from "../../../../../constants/routes";
import {
  NotificationTypes,
  useNotification,
} from "../../../../../hooks/useNotification";
import { getApiErrorMessage } from "../../../../../util/apiErrorMessage";
import { useAuthUserContext } from "../../../../../context/AdministratorContextProvider";

const durationItems = [
  {
    label: "Once",
    value: StripeCouponType.ONCE,
  },
  {
    label: "Repeating",
    value: StripeCouponType.REPEATING,
  },
  {
    label: "Forever",
    value: StripeCouponType.FOREVER,
  },
];

const Wrapper = styled.div``;

const Form = styled.form`
  background-color: #fff;
  padding: 30px;
  border: 1px solid #eeeeee;
  border-radius: 10px;
`;

const Content = styled.div`
  padding: 3rem calc((100vw - 1000px) / 2);
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;

  margin-top: 30px;
`;

const NameFormInput = styled(TextFormInput)`
  padding-top: 0;
`;

const Counters = styled.div``;

const CounterInput = styled.div`
  margin-top: 10px;
`;

const Label = styled(Typography)`
  margin-bottom: 5px;
`;

const StyledTypography = styled(Typography)`
  margin-top: 20px;
`;

const AddButton = styled(Button)`
  margin-top: 10px;
`;

const Modal = styled.div`
  display: flex;
  flex-direction: column;
  width: 90vw;
  max-width: 1200px;
  max-height: 90vh;
  background: #ffffff;
  border-radius: 5px;
  overflow: hidden;
`;

const ModalContent = styled.div`
  padding: 30px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

const AddedCompany = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid #eee;
  :last-child {
    border-bottom: none;
  }
  padding: 10px 0;
`;

const StyledCross = styled(Cross)`
  cursor: pointer;
`;

const AddedList = styled.div`
  margin: 10px 0;
`;

interface CreatePromoFormValues {
  name: string;
  percent_off: string;
  duration: StripeCouponType;
  duration_in_months: string;
  redeem_by: string;
  max_redemptions: string;
}

interface AddedCompanies {
  id?: string;
  name?: string;
}

const CreatePromitionCode: React.FunctionComponent = () => {
  const { authUser } = useAuthUserContext();
  const canCreate = authUser.policies?.actionsOnSystemSettings === true;
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { addModal, removeLastModal } = useModalManager();
  const { control, handleSubmit, watch } = useForm<CreatePromoFormValues>({
    mode: "onChange",
    resolver: yupResolver(promoCreateSchema),
  });
  const [discountPercent, setDiscountPersent] = useState(0);
  const [amountPercent, setAmountPersent] = useState(0);
  const [maxRedemptions, setMaxRedemptions] = useState(1);
  const [durationMonths, setDurationMonths] = useState(1);
  const [addedCompany, setAddedCompany] = useState<AddedCompanies>({});
  const [loading, setLoading] = useState(false);

  const durationTypeFormValue = watch("duration");

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const handleBack = () => {
    navigate(AppRoutes.Private.Subscription.PROMOTION_CODES);
  };

  const handleCreatePromo = async (values: CreatePromoFormValues) => {
    const { name, redeem_by: redeemBy, duration } = values;
    try {
      setLoading(true);
      await api.createPromoCode({
        name: name.replace(/\s/g, ""),
        redeem_by: new Date(redeemBy).getTime() / 1000,
        duration,
        duration_in_months: durationMonths,
        percent_off: discountPercent,
        amount_off: amountPercent,
        max_redemptions: maxRedemptions,
        companyId: addedCompany.id ?? "",
      });
      showNotification("Successfully created", NotificationTypes.SUCCESS);
      navigate(AppRoutes.Private.Subscription.PROMOTION_CODES);
      setLoading(false);
    } catch (e: unknown) {
      console.error(e);
      showNotification(
        getApiErrorMessage(e, "An error has occurred"),
        NotificationTypes.WARNING
      );
      setLoading(false);
    }
  };

  const handleAddCompany = (company: AddedCompanies) => {
    setAddedCompany(company);
  };

  const handleRemoveCompany = () => {
    setAddedCompany({});
  };

  const handleAddClick = () => {
    addModal(
      <Modal>
        <ModalHeader title="Select companies" handleClose={removeLastModal} />
        <ModalContent>
          <CompaniesList
            hasSubscriptionFilter
            withSelect
            onAddCompany={handleAddCompany}
          />
        </ModalContent>
      </Modal>
    );
  };

  if (!canCreate) {
    return (
      <Wrapper>
        <BackButton label="Back" onClick={handleBack} />
        <Content>
          <Typography variant={TypographyVariant.BODY1}>
            You do not have permission to create promotion codes.
          </Typography>
        </Content>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <BackButton label="Back" onClick={handleBack} />
      <Content>
        <Form onSubmit={handleSubmit(handleCreatePromo)}>
          <NameFormInput control={control} name="name" label="Name" required />
          <DatePickerFormField
            minDate={tomorrow}
            control={control}
            name="redeem_by"
            label="Activate before"
            required
          />
          <DropdownFormField
            control={control}
            name="duration"
            label="Duration type"
            items={durationItems}
            required
          />

          <CounterInput>
            <Label variant={TypographyVariant.HEADER3}>
              Duration in months
            </Label>
            <Counter
              count={durationMonths}
              min={1}
              disabled={durationTypeFormValue !== StripeCouponType.REPEATING}
              setCount={setDurationMonths}
              withCountInput
            />
          </CounterInput>

          <Counters>
            <CounterInput>
              <Label variant={TypographyVariant.HEADER3}>
                Discount percent
              </Label>
              <Counter
                count={discountPercent}
                min={0}
                // Cap at 100 — without it the counter input accepted
                // values like 500%, which the backend would happily
                // store and then refund 5x the price on redemption.
                max={100}
                setCount={(value) => {
                  // Hard-clamp on set in case the parent component is
                  // ever swapped for one that doesn't honour `max`.
                  const clamped = Math.max(
                    0,
                    Math.min(100, Number(value) || 0)
                  );
                  setDiscountPersent(clamped);
                  if (amountPercent !== 0) {
                    setAmountPersent(0);
                  }
                }}
                withCountInput
              />
            </CounterInput>
            <CounterInput>
              <Label variant={TypographyVariant.HEADER3}>Discount amount</Label>
              <Counter
                count={amountPercent}
                min={0}
                setCount={(value) => {
                  setAmountPersent(value);
                  if (discountPercent !== 0) {
                    setDiscountPersent(0);
                  }
                }}
                withCountInput
              />
            </CounterInput>
            <CounterInput>
              <Label variant={TypographyVariant.HEADER3}>
                Maximum redemptions
              </Label>
              <Counter
                count={maxRedemptions}
                setCount={setMaxRedemptions}
                min={1}
                withCountInput
              />
            </CounterInput>
          </Counters>
          <StyledTypography variant={TypographyVariant.HEADER2}>
            Company (Optional)
          </StyledTypography>
          {addedCompany.id && (
            <AddedList>
              <AddedCompany>
                <Typography variant={TypographyVariant.BODY1}>
                  {addedCompany.name} ({addedCompany.id})
                </Typography>
                <StyledCross
                  onClick={() => {
                    handleRemoveCompany();
                  }}
                />
              </AddedCompany>
            </AddedList>
          )}

          <AddButton
            type="button"
            variant={ButtonVariant.OUTLINED}
            onClick={handleAddClick}
          >
            Add company
          </AddButton>
          <ButtonWrapper>
            <Button loading={loading} variant={ButtonVariant.CONTAINED}>
              Create
            </Button>
          </ButtonWrapper>
        </Form>
      </Content>
    </Wrapper>
  );
};

export default CreatePromitionCode;
